import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import YouTube from 'react-youtube';
import { Document, Page, pdfjs } from 'react-pdf';
import { useCourseProgress } from '../contexts/CourseProgressContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeftIcon, 
  ArrowRightIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  DocumentTextIcon,
  PlayIcon,
  DocumentIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const extractYouTubeID = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const CourseContentPage = () => {
  const { identifier, moduleOrder: moduleOrderFromParams } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    modules: contextModules,
    completedModuleIds,
    lastAccessedModuleId,
    isModuleCompleted,
    markModuleAsCompleted,
    fetchCourseProgressAndModules,
    isLoading: progressIsLoading,
    error: progressError,
    currentCourseId: contextCourseId,
    courseTitle: contextCourseTitle,
    fetchedByIdentifier,
    needsPreTest,
    needsPostTest,
    getPostTestModule
  } = useCourseProgress();

  const [currentModule, setCurrentModule] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(-1);
  const [numPages, setNumPages] = useState(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  useEffect(() => {
    if (identifier && user && identifier !== fetchedByIdentifier) {
      fetchCourseProgressAndModules(identifier);
    } else if (!user && identifier) {
      setPageError("Anda harus login untuk mengakses konten kursus.");
      setPageLoading(false);
    }
  }, [identifier, user, fetchedByIdentifier, fetchCourseProgressAndModules]);

  useEffect(() => {
    if (progressIsLoading) {
      setPageLoading(true);
      return;
    }
    if (progressError) {
      setPageError(progressError);
      setPageLoading(false);
      return;
    }

    const CONTENT_MODULE_TYPES = ['PAGE', 'pdf', 'web', 'VIDEO', 'text', 'video'];
    const ALL_NAVIGABLE_MODULE_TYPES = [...CONTENT_MODULE_TYPES, 'PRE_TEST_QUIZ', 'POST_TEST_QUIZ', 'pre_test', 'post_test'];

    if (contextModules && contextModules.length > 0) {
      setPageError('');
      let targetModule = null;
      let targetIndex = -1;

      if (moduleOrderFromParams) {
        const order = parseInt(moduleOrderFromParams, 10);
        if (isNaN(order) || order < 1 || order > contextModules.length) {
          setPageError("Urutan modul tidak valid.");
          setPageLoading(false);
          return;
        }
        targetIndex = order - 1;
        targetModule = contextModules[targetIndex];
        
        if (!targetModule) {
          setPageError("Modul dengan urutan tersebut tidak ditemukan.");
          setPageLoading(false);
          return;
        }
        if (targetModule.type === 'PRE_TEST_QUIZ' || targetModule.type === 'pre_test') {
          navigate(`/course/${identifier}/pretest`);
          return;
        }
        if (targetModule.type === 'POST_TEST_QUIZ' || targetModule.type === 'post_test') {
          navigate(`/course/${identifier}/posttest`);
          return;
        }
      } else {
        const lastAccessedModule = contextModules.find(m => m.id === lastAccessedModuleId);
        if (lastAccessedModule) {
          const lastIndex = contextModules.findIndex(m => m.id === lastAccessedModuleId);
          if (lastIndex !== -1) {
            targetIndex = lastIndex;
            targetModule = lastAccessedModule;
          }
        }
        
        if (!targetModule) {
          const firstContentModule = contextModules.find(m => CONTENT_MODULE_TYPES.includes(m.type));
          if (firstContentModule) {
            const firstIndex = contextModules.findIndex(m => m.id === firstContentModule.id);
            targetIndex = firstIndex;
            targetModule = firstContentModule;
          }
        }
      }

      if (!targetModule) {
        setPageError("Tidak ada modul konten yang ditemukan.");
        setPageLoading(false);
        return;
      }

      setCurrentModule(targetModule);
      setCurrentModuleIndex(targetIndex);
      setNumPages(null); 
      setCurrentPageNumber(1);

      if (CONTENT_MODULE_TYPES.includes(targetModule.type)) {
        if (!isModuleCompleted(targetModule.id)) {
          markModuleAsCompleted(targetModule.id);
        }
      }
      setPageLoading(false);
    } else {
      setPageError("Tidak ada modul konten yang ditemukan untuk kursus ini.");
      setCurrentModule(null);
      setPageLoading(false);
    }
  }, [
    identifier,
    moduleOrderFromParams,
    contextModules, 
    progressIsLoading,
    progressError,
    user,
    fetchCourseProgressAndModules,
    navigate,
    lastAccessedModuleId,
    isModuleCompleted,
    markModuleAsCompleted,
    fetchedByIdentifier
  ]);

  const CONTENT_MODULE_TYPES_FOR_NAV = ['PAGE', 'pdf', 'web', 'VIDEO', 'text', 'video'];

  const getNextAction = () => {
    if (!currentModule || currentModuleIndex === -1 || !contextModules || contextModules.length === 0) {
      return { text: 'Modul Selanjutnya', action: 'next' };
    }

    const totalModulesInCourse = contextModules ? contextModules.length : 0;
    const isLastModule = currentModuleIndex === totalModulesInCourse - 1;
    
    if (!isLastModule) {
      return { text: 'Modul Selanjutnya', action: 'next' };
    }

    const postTestModule = getPostTestModule();
    
    if (needsPostTest && postTestModule && !isModuleCompleted(postTestModule.id)) {
      return { text: 'Lanjut ke Post-Test', action: 'posttest' };
    } else {
      return { text: 'Selesai', action: 'finish' };
    }
  };

  const handleNextModule = () => {
    if (!currentModule || currentModuleIndex === -1 || !contextModules || contextModules.length === 0) return;

    if (CONTENT_MODULE_TYPES_FOR_NAV.includes(currentModule.type) && !isModuleCompleted(currentModule.id)) {
      markModuleAsCompleted(currentModule.id);
    }

    const nextAction = getNextAction();
    
    if (nextAction.action === 'finish') {
      navigate(`/course/${identifier}`);
      return;
    }
    
    if (nextAction.action === 'posttest') {
      navigate(`/course/${identifier}/posttest`);
      return;
    }

    let nextNavigableModule = null;
    let nextModuleOrder = -1;
    for (let i = currentModuleIndex + 1; i < contextModules.length; i++) {
      const mod = contextModules[i];
      if (['PAGE', 'pdf', 'web', 'VIDEO', 'text', 'video', 'PRE_TEST_QUIZ', 'pre_test', 'POST_TEST_QUIZ', 'post_test'].includes(mod.type)) {
        nextNavigableModule = mod;
        nextModuleOrder = i + 1;
        break;
      }
    }

    if (nextNavigableModule) {
      if (nextNavigableModule.type === 'PRE_TEST_QUIZ' || nextNavigableModule.type === 'pre_test') {
        navigate(`/course/${identifier}/pretest`);
      } else if (nextNavigableModule.type === 'POST_TEST_QUIZ' || nextNavigableModule.type === 'post_test') {
        navigate(`/course/${identifier}/posttest`);
      } else {
        navigate(`/course/${identifier}/content/${nextModuleOrder}`);
      }
    }
  };

  const handlePreviousModule = () => {
    if (!currentModule || currentModuleIndex <= 0 || !contextModules || contextModules.length === 0) return;

    let prevNavigableModule = null;
    let prevModuleOrder = -1;
    for (let i = currentModuleIndex - 1; i >= 0; i--) {
      const mod = contextModules[i];
      if (['PAGE', 'pdf', 'web', 'VIDEO', 'text', 'video', 'PRE_TEST_QUIZ', 'pre_test', 'POST_TEST_QUIZ', 'post_test'].includes(mod.type)) {
        prevNavigableModule = mod;
        prevModuleOrder = i + 1;
        break;
      }
    }

    if (prevNavigableModule) {
      if (prevNavigableModule.type === 'PRE_TEST_QUIZ' || prevNavigableModule.type === 'pre_test') {
        navigate(`/course/${identifier}/pretest`);
      } else if (prevNavigableModule.type === 'POST_TEST_QUIZ' || prevNavigableModule.type === 'post_test') {
        navigate(`/course/${identifier}/posttest`);
      } else {
        navigate(`/course/${identifier}/content/${prevModuleOrder}`);
      }
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPreviousPage = () => {
    setCurrentPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const totalModulesInCourse = contextModules ? contextModules.length : 0;
  const completedCount = completedModuleIds ? completedModuleIds.size : 0;

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat konten...</p>
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Terjadi Kesalahan</h3>
            <p className="text-red-700">{pageError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DocumentTextIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Modul Tidak Ditemukan</h3>
            <p className="text-gray-600">Modul yang Anda cari tidak tersedia.</p>
          </div>
        </div>
      </div>
    );
  }

  const getModuleIcon = (type) => {
    switch (type) {
      case 'CONTENT':
      case 'PAGE':
        return <DocumentTextIcon className="w-6 h-6" />;
      case 'VIDEO':
      case 'video':
        return <PlayIcon className="w-6 h-6" />;
      case 'PDF':
      case 'pdf':
        return <DocumentIcon className="w-6 h-6" />;
      default:
        return <DocumentTextIcon className="w-6 h-6" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/course/${identifier}/modules`}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Kembali ke Daftar Modul</span>
          </Link>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  {getModuleIcon(currentModule.type)}
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-800">{currentModule.judul}</h1>
                  <p className="text-gray-600">Modul {currentModuleIndex + 1} dari {totalModulesInCourse}</p>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-sm text-gray-600 mb-1">Progress</div>
                <div className="text-lg font-semibold text-blue-600">{completedCount} / {totalModulesInCourse}</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${totalModulesInCourse > 0 ? (completedCount / totalModulesInCourse) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {currentModule.type === 'PAGE' && currentModule.initialContent && (
            <div className="p-6 md:p-8">
              <div className="ql-snow">
                <div className="ql-editor prose max-w-none" dangerouslySetInnerHTML={{ __html: currentModule.initialContent }} />
              </div>
            </div>
          )}
          
          {currentModule.type === 'PAGE' && !currentModule.initialContent && currentModule.contentText && (
            <div className="p-6 md:p-8">
              <div className="prose max-w-none">
                <p>{currentModule.contentText}</p>
              </div>
            </div>
          )}
          
          {currentModule.type === 'pdf' && currentModule.pdfPath && (
            <div className="p-6 md:p-8">
              <div className="prose max-w-none mb-6">
                <h2 className="text-xl font-semibold">{currentModule.judul}</h2>
              </div>
              <div className="w-full bg-gray-100 rounded-xl overflow-hidden flex flex-col" style={{ height: '1200px' }}>
                <Document
                  file={currentModule.pdfPath}
                  className="flex-grow min-h-0 flex flex-col"
                  loading={
                    <div className="flex justify-center items-center flex-grow"> 
                      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
                    </div>
                  }
                  error={
                    <div className="flex justify-center items-center flex-grow text-gray-600"> 
                      Gagal memuat PDF. Pastikan file tersedia dan dalam format yang benar.
                    </div>
                  }
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  <Page
                    pageNumber={currentPageNumber}
                    height={1170}
                    className="w-full h-full flex flex-col"
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={
                      <div className="flex justify-center items-center flex-grow text-gray-600">
                        Memuat halaman...
                      </div>
                    }
                    error={
                      <div className="flex justify-center items-center flex-grow text-gray-600">
                        Gagal memuat halaman PDF.
                      </div>
                    }
                  />
                </Document>
                
                {numPages && (
                  <div className="mt-4 flex justify-center items-center space-x-4 p-4 bg-white border-t border-gray-200">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPageNumber <= 1}
                      className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <ChevronLeftIcon className="w-4 h-4 mr-1" />
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-600 font-medium">
                      Halaman {currentPageNumber} dari {numPages}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={currentPageNumber >= numPages}
                      className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Selanjutnya
                      <ChevronRightIcon className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {(currentModule.type === 'web' || currentModule.type === 'video') && currentModule.videoLink && (
            <div className="p-6 md:p-8">
              <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-lg">
                <YouTube
                  videoId={extractYouTubeID(currentModule.videoLink)} 
                  opts={{
                    height: '100%',
                    width: '100%',
                    playerVars: {
                      autoplay: 0,
                      modestbranding: 1,
                      rel: 0,
                    },
                  }}
                  className="w-full h-full"
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <button
            onClick={handlePreviousModule}
            disabled={currentModuleIndex <= 0 || totalModulesInCourse === 0}
            className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" />
            Modul Sebelumnya
          </button>
          
          <button
            onClick={handleNextModule}
            disabled={totalModulesInCourse === 0}
            className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {getNextAction().text}
            <ChevronRightIcon className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseContentPage;
